import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input } from "@/ui/primitives.tsx";

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for destructive actions. */
  danger?: boolean;
  /** A second action beside the confirm one (choose mode): choose() resolves with "alt" when it is picked. */
  altLabel?: string;
  /** When set, the dialog asks for a text value (prompt mode) and resolves with it. */
  input?: { label?: string; initial?: string; placeholder?: string };
}

type Ask = (o: ConfirmOptions) => Promise<boolean>;
type Prompt = (o: ConfirmOptions & { input: NonNullable<ConfirmOptions["input"]> }) => Promise<string | null>;
type Choose = (o: ConfirmOptions & { altLabel: string }) => Promise<"confirm" | "alt" | null>;
const Ctx = createContext<{ confirm: Ask; prompt: Prompt; choose: Choose } | null>(null);

/** App-wide confirmation/prompt modal: `const { confirm } = useConfirm(); if (await confirm({...})) ...` */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<ConfirmOptions | null>(null);
  const [value, setValue] = useState("");
  const resolver = useRef<((v: boolean | string | null) => void) | null>(null);
  const close = (v: boolean | string | null) => { resolver.current?.(v); resolver.current = null; setOpen(null); };
  const confirm = useCallback<Ask>((o) => new Promise((res) => { resolver.current = (v) => res(v === true); setValue(""); setOpen(o); }), []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(open.input ? null : false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  const choose = useCallback<Choose>((o) => new Promise((res) => { resolver.current = (v) => res(v === true ? "confirm" : v === "alt" ? "alt" : null); setValue(""); setOpen(o); }), []);
  const prompt = useCallback<Prompt>((o) => new Promise((res) => { resolver.current = (v) => res(typeof v === "string" ? v : null); setValue(o.input.initial ?? ""); setOpen(o); }), []);
  return (
    <Ctx.Provider value={{ confirm, prompt, choose }}>
      {children}
      {open && (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-black/55 p-4" onClick={() => close(open.input ? null : false)}>
          <form role="dialog" aria-modal="true" className="w-full max-w-md space-y-4 rounded-xl border border-border bg-surface p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); close(open.input ? value : true); }}>
            <div>
              <h2 className="text-base font-semibold">{open.title}</h2>
              {open.message && <div className="mt-1 text-sm text-fg-muted">{open.message}</div>}
            </div>
            {open.input && (
              <label className="block text-sm">
                {open.input.label && <span className="mb-1 block font-medium">{open.input.label}</span>}
                <Input autoFocus value={value} placeholder={open.input.placeholder} onChange={(e) => setValue(e.target.value)} />
              </label>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => close(open.input ? null : false)}>{open.cancelLabel ?? t("common.cancel")}</Button>
              {open.altLabel && <Button type="button" variant="secondary" onClick={() => close("alt")}>{open.altLabel}</Button>}
              {/* with two actions (one of them destructive) Enter must not pick one: nothing is focused */}
              <Button type="submit" autoFocus={!open.input && !open.altLabel} variant={open.danger ? "danger" : "primary"} disabled={!!open.input && !value.trim()}>{open.confirmLabel ?? t("common.confirm")}</Button>
            </div>
          </form>
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("ConfirmProvider missing");
  return ctx;
}
